// Two-party-consent (all-party consent) US states for the browser dialer.
// When a call is placed to one of these states and recording is active,
// we must notify the other party that the call is being recorded.
export const TWO_PARTY_CONSENT_STATES = ["CA", "CT", "FL", "IL", "MD", "MA", "MT", "NV", "NH", "PA", "WA"] as const;

// Area code → US state abbreviation mapping (US only).
// Covers all NANP area codes assigned to two-party-consent states.
const AREA_CODE_TO_STATE: Record<string, string> = {
  // California (CA)
  "209": "CA", "213": "CA", "279": "CA", "310": "CA", "323": "CA",
  "341": "CA", "408": "CA", "415": "CA", "424": "CA", "442": "CA",
  "510": "CA", "530": "CA", "559": "CA", "562": "CA", "619": "CA",
  "626": "CA", "628": "CA", "650": "CA", "657": "CA", "661": "CA",
  "669": "CA", "707": "CA", "714": "CA", "747": "CA", "760": "CA",
  "805": "CA", "818": "CA", "820": "CA", "831": "CA", "858": "CA",
  "909": "CA", "916": "CA", "925": "CA", "949": "CA", "951": "CA",
  // Connecticut (CT)
  "203": "CT", "475": "CT", "860": "CT", "959": "CT",
  // Florida (FL)
  "239": "FL", "305": "FL", "321": "FL", "352": "FL", "386": "FL",
  "407": "FL", "448": "FL", "561": "FL", "656": "FL", "689": "FL",
  "727": "FL", "754": "FL", "772": "FL", "786": "FL", "813": "FL",
  "850": "FL", "863": "FL", "904": "FL", "941": "FL", "954": "FL",
  // Illinois (IL)
  "217": "IL", "224": "IL", "309": "IL", "312": "IL", "331": "IL",
  "447": "IL", "464": "IL", "618": "IL", "630": "IL", "708": "IL",
  "730": "IL", "773": "IL", "779": "IL", "815": "IL", "847": "IL",
  "872": "IL",
  // Maryland (MD)
  "240": "MD", "301": "MD", "410": "MD", "443": "MD", "667": "MD",
  // Massachusetts (MA)
  "339": "MA", "351": "MA", "413": "MA", "508": "MA", "617": "MA",
  "774": "MA", "781": "MA", "857": "MA", "978": "MA",
  // Montana (MT)
  "406": "MT",
  // Nevada (NV)
  "702": "NV", "725": "NV", "775": "NV",
  // New Hampshire (NH)
  "603": "NH",
  // Pennsylvania (PA)
  "215": "PA", "223": "PA", "267": "PA", "272": "PA", "412": "PA",
  "445": "PA", "484": "PA", "570": "PA", "582": "PA", "610": "PA",
  "717": "PA", "724": "PA", "814": "PA", "835": "PA", "878": "PA",
  // Washington (WA)
  "206": "WA", "253": "WA", "360": "WA", "425": "WA", "509": "WA",
  "564": "WA",
};

/**
 * Returns true when the phone number or area code belongs to one of the 11
 * two-party-consent US states.
 *
 * @param numberOrAreaCode  An E.164 number (+15551234567), a 10-digit number,
 *                          or a raw 3-digit area code string.
 */
export function isConsentState(numberOrAreaCode: string): boolean {
  const digits = numberOrAreaCode.replace(/\D/g, "");
  let areaCode: string;
  if (digits.length === 3) {
    areaCode = digits;
  } else if (digits.length === 10) {
    areaCode = digits.slice(0, 3);
  } else if (digits.length === 11 && digits.startsWith("1")) {
    areaCode = digits.slice(1, 4);
  } else {
    return false;
  }
  const state = AREA_CODE_TO_STATE[areaCode];
  return !!state && (TWO_PARTY_CONSENT_STATES as readonly string[]).includes(state);
}
